import { Ticket } from './types';

/**
 * 그룹 수강권 상품 고르기용 필터.
 *
 * 상품 목록은 서버가 센터 전체를 한 번에 내려준다(검색·페이지네이션 없음).
 * 상품이 수십 개인 센터에서는 카드 그리드만으로는 고를 수 없으므로
 * 이름 검색과 수업 유형 필터를 화면에 붙이고, 그 판정은 여기서 한다.
 */

export type GroupTicketClassKey = 'PRIVATE_1ON2' | 'PRIVATE_1ON3' | 'GROUP';

export const GROUP_TICKET_CLASS_LABELS: Record<GroupTicketClassKey, string> = {
    PRIVATE_1ON2: '1:2',
    PRIVATE_1ON3: '1:3',
    GROUP: '그룹',
};

/** classType이 비어 있는 레거시 상품은 그룹으로 읽는다(그룹 수강권 목록이므로). */
export const getGroupTicketClassKey = (ticket: Pick<Ticket, 'classType'>): GroupTicketClassKey => {
    const raw = String(ticket?.classType || '');
    return raw === 'PRIVATE_1ON2' || raw === 'PRIVATE_1ON3' ? raw : 'GROUP';
};

/**
 * 실제로 존재하는 수업 유형만 필터 칩으로 만든다.
 * 결과가 0개인 칩을 띄우면 "눌러도 아무것도 없는 버튼"이 된다.
 */
export const buildGroupTicketClassFilters = (
    tickets: Ticket[],
): Array<{ key: GroupTicketClassKey; label: string; count: number }> => {
    const countByKey = new Map<GroupTicketClassKey, number>();
    tickets.forEach((ticket) => {
        const key = getGroupTicketClassKey(ticket);
        countByKey.set(key, (countByKey.get(key) || 0) + 1);
    });

    // 라벨 정의 순서를 따른다. 목록을 다시 열 때마다 칩 순서가 바뀌면 근육 기억이 깨진다.
    return (Object.keys(GROUP_TICKET_CLASS_LABELS) as GroupTicketClassKey[])
        .filter((key) => (countByKey.get(key) || 0) > 0)
        .map((key) => ({ key, label: GROUP_TICKET_CLASS_LABELS[key], count: countByKey.get(key) || 0 }));
};

/** 공백·대소문자 차이로 검색이 빗나가지 않게 한다. "10:1 그룹수업" vs "10:1그룹수업" */
const normalize = (value: string) => value.toLowerCase().replace(/\s+/g, '');

export const filterGroupTickets = ({
    tickets,
    search = '',
    classFilter = null,
}: {
    tickets: Ticket[];
    search?: string;
    /** null이면 전체. */
    classFilter?: GroupTicketClassKey | null;
}): Ticket[] => {
    const keyword = normalize(search.trim());
    return tickets.filter((ticket) => {
        if (classFilter && getGroupTicketClassKey(ticket) !== classFilter) return false;
        if (!keyword) return true;
        // 이름 외에 회차로도 찾게 한다 — "10회"로 기억하는 경우가 흔하다.
        const haystack = normalize(
            [ticket.name || '', ticket.totalCount != null ? `${ticket.totalCount}회` : ''].join(' '),
        );
        return haystack.includes(keyword);
    });
};

/**
 * 한 번에 그리는 카드 수 상한.
 * 상품이 수백 개인 센터에서 전부 DOM에 올리면 모달 여는 순간 버벅인다.
 * 넘치는 만큼은 화면이 "몇 개가 더 있는지" 밝히고 검색으로 좁히게 안내한다.
 */
export const GROUP_TICKET_RENDER_LIMIT = 60;

export const limitGroupTickets = (
    tickets: Ticket[],
    selectedTicketId = '',
): { visible: Ticket[]; hiddenCount: number } => {
    if (tickets.length <= GROUP_TICKET_RENDER_LIMIT) {
        return { visible: tickets, hiddenCount: 0 };
    }
    const visible = tickets.slice(0, GROUP_TICKET_RENDER_LIMIT);
    // 이미 고른 상품이 상한 밖으로 밀리면 "선택이 사라진 것처럼" 보인다. 끌어올린다.
    if (selectedTicketId && !visible.some((ticket) => ticket._id === selectedTicketId)) {
        const selected = tickets.find((ticket) => ticket._id === selectedTicketId);
        if (selected) visible[visible.length - 1] = selected;
    }
    return { visible, hiddenCount: tickets.length - GROUP_TICKET_RENDER_LIMIT };
};
